import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { InstallShortcutButton } from "@/components/InstallShortcutButton";
import { ContactSection } from "@/components/ContactSection";
import {
  Store, ArrowRight, ArrowUpRight, CheckCircle2, Boxes, Truck, Calculator, ClipboardCheck,
  BarChart3, Receipt, Clock, Users, Factory, Warehouse, Building2, ShoppingCart, Layers, Handshake,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/cb43827a-07c3-48c9-a9b5-73ab0995a462/images/0af751756d84e607c9e3d6bfc0a8717763df063de7c3622f74dd914033bd98d1.jpeg";

const fade = (d = 0) => ({ initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] } });

const TYPES = [
  { key: "manufacturers", icon: Factory, name: "Manufacturers", line: "Produce to real demand, not guesses.",
    steps: ["See every partner's orders summed up by product, each morning", "Compare demand with stock on hand and spot shortages instantly", "Set a daily ordering window and close it at your cut-off", "Upload invoices and track who still owes you"] },
  { key: "distributors", icon: Truck, name: "Distributors", line: "Buy from makers, sell to the trade — in one place.",
    steps: ["Order from manufacturers with prices filled in automatically", "Receive wholesaler orders and accept, adjust or decline them", "Delivered stock flows into inventory by itself", "Partner-by-partner dues so follow-ups are obvious"] },
  { key: "wholesalers", icon: Warehouse, name: "Wholesalers", line: "Keep shelves stocked and margins visible.",
    steps: ["Re-order from distributors in a few taps", "Supply retailers from your own catalogue", "Record daily usage and see what's left", "Compare spend and earnings period over period"] },
  { key: "retailers", icon: ShoppingCart, name: "Retailers", line: "Order supplies without the phone calls.",
    steps: ["Browse connected suppliers' live catalogues", "Get notified when out-of-stock items return", "Confirm deliveries or report shortages item by item", "Download every invoice whenever you need it"] },
];

const FEATURES = [
  { icon: Boxes, title: "Inventory & Product Catalogs", desc: "Your own categories, products and units — with stock on hand that updates itself on delivery.", testid: "feature-inventory-catalog" },
  { icon: Truck, title: "Ordering & Delivery", desc: "Every order moves from New to Delivered with both sides seeing the same status, live.", testid: "feature-ordering-delivery" },
  { icon: Calculator, title: "Automatic Product Pricing", desc: "Set unit prices once. Line totals and the full bill are calculated for you.", testid: "feature-automatic-pricing" },
  { icon: ClipboardCheck, title: "Inventory Tracking", desc: "Incoming stock is added automatically; daily usage is a simple +/− away.", testid: "feature-inventory-tracking" },
  { icon: Receipt, title: "Invoices & Payment Dues", desc: "Attach invoices to orders, mark them paid or pending, and send one-tap reminders.", testid: "feature-expense-earnings" },
  { icon: BarChart3, title: "Smart Financial Insights", desc: "Spend and earnings by day, week or month, compared against the previous period.", testid: "feature-financial-insights" },
];

const PILLARS = [
  { n: "01", title: "Simplify daily operations", desc: "Catalogues, orders, pricing and stock in one flow — no spreadsheets, chats or manual bills.", testid: "pillar-simplify" },
  { n: "02", title: "Full supply visibility", desc: "Buyer and supplier share the same order, quantities and totals from request to delivery.", testid: "pillar-visibility" },
  { n: "03", title: "Decisions backed by data", desc: "Demand summaries, shortages, dues and price trends help you buy and sell with confidence.", testid: "pillar-decisions" },
];

function Logo({ small }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`flex items-center justify-center rounded-xl bg-[#0D5C3A] text-white ${small ? "h-8 w-8" : "h-9 w-9"}`}><Store className="h-4 w-4" /></div>
      <span className={`whitespace-nowrap font-heading font-extrabold tracking-tight text-[#0D5C3A] ${small ? "hidden text-lg min-[400px]:inline" : "text-xl"}`}>RESTRO<span className="text-[#C85A32]">-VENDS</span></span>
    </div>
  );
}

function Nav({ go }) {
  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6">
      <div className="mx-auto flex max-w-7xl items-center justify-between rounded-full border border-white/60 bg-white/70 px-4 py-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.05)] backdrop-blur-xl sm:px-6">
        <Logo small />
        <nav className="hidden items-center gap-8 md:flex">
          {[["#business-types", "Business Types", "nav-types-link"], ["#features", "Features", "nav-features-link"], ["#why", "Why Us", "nav-why-link"], ["#contact", "Talk to us", "nav-contact-link"]].map(([h, l, t]) => (
            <a key={h} href={h} data-testid={t} className="text-sm font-medium text-stone-600 transition-colors hover:text-[#0D5C3A]">{l}</a>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button variant="ghost" className="rounded-full px-3 sm:px-4" data-testid="nav-login-btn" onClick={() => go("/login")}>Sign In</Button>
          <Button data-testid="nav-register-btn" className="rounded-full bg-[#0D5C3A] px-4 transition-transform hover:scale-[1.03] hover:bg-[#084229] sm:px-5" onClick={() => go("/register")}>Get Started</Button>
          <InstallShortcutButton />
        </div>
      </div>
    </header>
  );
}

function Hero({ go }) {
  return (
    <section className="relative overflow-hidden pt-32 sm:pt-40">
      <div className="pointer-events-none absolute -right-40 top-10 h-[34rem] w-[34rem] rounded-full bg-[#0D5C3A]/[0.07] blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-72 h-96 w-96 rounded-full bg-[#C85A32]/[0.07] blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:gap-10">
        <motion.div className="lg:col-span-6 lg:pt-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C85A32]">B2B supply · inventory · finance</p>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
            One network for every business in your <span className="relative whitespace-nowrap text-[#0D5C3A]">supply chain<svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" preserveAspectRatio="none"><path d="M0 6 Q100 0 200 6" stroke="#C85A32" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg></span>.
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Manufacturers, distributors, wholesalers and retailers connect, order, deliver and get paid — with pricing, stock and dues kept accurate for both sides.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button size="lg" data-testid="hero-register-btn" className="group h-12 rounded-full bg-[#0D5C3A] px-7 text-base transition-transform hover:scale-[1.02] hover:bg-[#084229]" onClick={() => go("/register")}>
              Register your business <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button size="lg" variant="ghost" data-testid="hero-login-btn" className="h-12 rounded-full px-6 text-base text-stone-700 hover:bg-white" onClick={() => go("/login")}>
              I already have an account
            </Button>
            <a href="#contact" data-testid="hero-contact-link" className="px-2 text-sm font-medium text-[#0D5C3A] underline-offset-4 hover:underline">Questions? Talk to us</a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-[#E5E0D8] pt-6 text-sm text-stone-500">
            {["Free to get started", "Admin-verified businesses", "Works for buyers and sellers"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#0D5C3A]" /> {t}</span>
            ))}
          </div>
        </motion.div>
        <motion.div className="relative lg:col-span-6" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
          <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(13,92,58,0.18)]">
            <img src={HERO_IMG} alt="Warehouse inventory and supply-chain logistics" className="h-[22rem] w-full object-cover sm:h-[30rem]" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b2e20]/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <div><p className="text-xs uppercase tracking-[0.18em] text-white/70">Order window</p><p className="font-heading text-xl font-bold">Open · closes 10:00 AM</p></div>
              <Clock className="h-6 w-6 text-white/80" />
            </div>
          </div>
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-3 top-10 w-52 rounded-2xl border border-white/70 bg-white/80 p-4 shadow-2xl backdrop-blur-xl sm:-left-10">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-stone-400"><Receipt className="h-3.5 w-3.5 text-[#D97706]" /> Example order total</p>
            <p className="mt-1 font-heading text-2xl font-extrabold text-[#0D5C3A]">₹4,250.00</p>
            <p className="text-xs text-stone-500">auto-calculated from 6 items</p>
          </motion.div>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-2 top-1/2 rounded-full border border-white/70 bg-white/85 px-4 py-2.5 shadow-xl backdrop-blur-xl sm:-right-6">
            <span className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Delivered · stock updated</span>
          </motion.div>
        </motion.div>
      </div>
      <Ribbon />
    </section>
  );
}

function Ribbon() {
  const items = [
    { v: "4 types", l: "Manufacturers to retailers", t: "metric-roles" },
    { v: "Auto", l: "Totals, stock & dues", t: "metric-auto" },
    { v: "Live", l: "Order status for both sides", t: "metric-live" },
    { v: "Verified", l: "Admin-checked businesses", t: "metric-verified" },
  ];
  return (
    <div className="relative mx-auto mt-20 max-w-7xl px-6 sm:mt-28">
      <div className="grid grid-cols-2 divide-[#E5E0D8] border-y border-[#E5E0D8] sm:grid-cols-4 sm:divide-x">
        {items.map((m, i) => (
          <motion.div key={m.t} {...fade(i * 0.06)} data-testid={m.t} className="py-7 sm:px-8">
            <p className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">{m.v}</p>
            <p className="mt-1 text-sm text-stone-500">{m.l}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function BusinessTypes() {
  const [active, setActive] = useState(TYPES[0].key);
  const t = TYPES.find((x) => x.key === active);
  return (
    <section id="business-types" className="mx-auto max-w-7xl scroll-mt-28 px-6 py-24 sm:py-32">
      <motion.div {...fade()} className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C85A32]">Built for every link</p>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">Every business buys and sells. RESTRO-VENDS handles both.</h2>
      </motion.div>
      <div className="mt-14 grid gap-12 lg:grid-cols-12">
        <div className="flex gap-2 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:gap-1 lg:overflow-visible">
          {TYPES.map((x) => (
            <button key={x.key} onClick={() => setActive(x.key)} data-testid={`business-tab-${x.key}`}
              className={`group relative flex shrink-0 items-center gap-3 rounded-full px-5 py-3 text-left transition-colors lg:rounded-none lg:border-l-2 lg:px-6 lg:py-4 ${active === x.key ? "bg-[#0D5C3A] text-white lg:border-[#0D5C3A] lg:bg-transparent lg:text-[#0D5C3A]" : "bg-white text-stone-600 hover:text-stone-900 lg:border-[#E5E0D8] lg:bg-transparent"}`}>
              <x.icon className="h-5 w-5" />
              <span className="font-heading text-base font-bold lg:text-lg">{x.name}</span>
            </button>
          ))}
        </div>
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div key={t.key} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.3 }} data-testid={`business-panel-${t.key}`}>
              <p className="font-heading text-2xl font-bold text-stone-900 sm:text-3xl">{t.line}</p>
              <ol className="mt-8 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
                {t.steps.map((s, i) => (
                  <li key={s} className="flex items-baseline gap-6 py-5">
                    <span className="font-mono text-sm text-[#C85A32]">0{i + 1}</span>
                    <span className="text-base text-stone-700 sm:text-lg">{s}</span>
                  </li>
                ))}
              </ol>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="scroll-mt-28 bg-[#0D5C3A] py-24 text-white sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-10 lg:grid-cols-12">
          <motion.div {...fade()} className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#F4B183]">Everything connected</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">The whole supply loop, from catalogue to cash.</h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">Each feature feeds the next — an order updates stock, an invoice updates dues, and your reports stay right without extra work.</p>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm text-white/80">
              <span className="inline-flex items-center gap-2"><Handshake className="h-4 w-4 text-[#F4B183]" /> Partner network</span>
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-[#F4B183]" /> Team roles & activity</span>
              <span className="inline-flex items-center gap-2"><Layers className="h-4 w-4 text-[#F4B183]" /> Daily demand summary</span>
            </div>
          </motion.div>
          <div className="grid gap-x-10 sm:grid-cols-2 lg:col-span-7">
            {FEATURES.map((f, i) => (
              <motion.div key={f.testid} {...fade((i % 2) * 0.08)} data-testid={f.testid} className="group border-t border-white/15 py-8">
                <div className="flex items-center justify-between">
                  <f.icon className="h-6 w-6 text-[#F4B183]" />
                  <ArrowUpRight className="h-4 w-4 text-white/30 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section id="why" className="mx-auto max-w-7xl scroll-mt-28 px-6 py-24 sm:py-32">
      <motion.div {...fade()} className="grid gap-6 lg:grid-cols-12">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0D5C3A] lg:col-span-4">Why RESTRO-VENDS</p>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl lg:col-span-8">Supply between businesses usually lives in calls, chats and notebooks. We put it in one trusted flow.</h2>
      </motion.div>
      <div className="mt-16 grid md:grid-cols-3 md:divide-x md:divide-[#E5E0D8]">
        {PILLARS.map((p, i) => (
          <motion.div key={p.n} {...fade(i * 0.1)} data-testid={p.testid} className="border-t border-[#E5E0D8] py-8 md:border-t-0 md:px-8 md:first:pl-0">
            <span className="font-mono text-sm text-[#C85A32]">{p.n}</span>
            <h3 className="mt-4 font-heading text-xl font-bold text-stone-900">{p.title}</h3>
            <p className="mt-3 text-base leading-relaxed text-stone-600">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function FinalCta({ go }) {
  return (
    <section className="px-3 pb-6 sm:px-6">
      <motion.div {...fade()} className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-[#10251b] px-8 py-20 sm:px-16">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#D97706]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-80 w-80 rounded-full bg-[#0D5C3A]/50 blur-3xl" />
        <div className="relative grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Building2 className="h-8 w-8 text-[#F4B183]" />
            <h2 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-5xl">Bring your supply chain onto one platform.</h2>
            <p className="mt-5 max-w-xl text-base text-white/70">Join as a manufacturer, distributor, wholesaler or retailer and run buying and selling from one clean dashboard.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
            <Button size="lg" data-testid="footer-register-cta" className="h-12 rounded-full bg-white px-7 text-[#0D5C3A] transition-transform hover:scale-[1.03] hover:bg-[#FAF8F5]" onClick={() => go("/register")}>
              Register your business <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="ghost" data-testid="footer-signin-cta" className="h-12 rounded-full px-6 text-white hover:bg-white/10 hover:text-white" onClick={() => go("/login")}>Sign in</Button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function Footer({ go }) {
  return (
    <footer className="mx-auto max-w-7xl px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <Logo small />
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-stone-500">
          <button onClick={() => go("/register")} className="transition-colors hover:text-[#0D5C3A]" data-testid="footer-register-link">Register</button>
          <button onClick={() => go("/login")} className="transition-colors hover:text-[#0D5C3A]" data-testid="footer-login-link">Business Login</button>
          <a href="#contact" className="transition-colors hover:text-[#0D5C3A]" data-testid="footer-contact-link">Talk to us</a>
          <a href="/terms" className="transition-colors hover:text-[#0D5C3A]" data-testid="footer-terms-link">Terms</a>
          <a href="/privacy" className="transition-colors hover:text-[#0D5C3A]" data-testid="footer-privacy-link">Privacy</a>
        </div>
      </div>
      <p className="mt-8 border-t border-[#E5E0D8] pt-6 text-xs text-stone-400">© {new Date().getFullYear()} RESTRO-VENDS. All rights reserved.</p>
    </footer>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAF8F5] rv-grain" data-testid="landing-page">
      <Nav go={navigate} />
      <Hero go={navigate} />
      <BusinessTypes />
      <Features />
      <Why />
      <ContactSection />
      <FinalCta go={navigate} />
      <Footer go={navigate} />
    </div>
  );
}
